import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { v2MenuItemDangerClass, V2ThemeProvider } from '@onevision-resources/design-theme';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { useWorkflowVersionMutations } from 'src/graphql/entities/workflow';
import type { WorkflowVersionRaw } from 'src/types';
import { useConfirmationModal, useNotifications } from 'src/utils/hooks';

interface WorkflowVersionActionsMenuProps {
  partnerId?: number | null;
  workflowName: string;
  selectedVersion: Pick<
    WorkflowVersionRaw,
    'workflowVersionId' | 'version' | 'isActive' | 'deletedAt'
  >;
  workflowVersions: Pick<WorkflowVersionRaw, 'workflowVersionId' | 'deletedAt'>[];
  refetchWorkflowVersions?: () => Promise<unknown>;
  onDeleteSuccess?: (deletedWorkflowVersionId: string) => void;
  viewOnly?: boolean;
  /**
   * The draft has edits on the canvas that aren't saved. Activate would put the stored definition
   * live and drop them, so it asks for a save or a discard first.
   */
  hasUnsavedChanges?: boolean;
}

export const WorkflowVersionActionsMenu = ({
  partnerId,
  workflowName,
  selectedVersion,
  workflowVersions,
  refetchWorkflowVersions,
  onDeleteSuccess,
  viewOnly = false,
  hasUnsavedChanges = false,
}: WorkflowVersionActionsMenuProps) => {
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const { confirm } = useConfirmationModal();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { changeWorkflowVersionActiveState, softDeleteWorkflowVersion, loading } =
    useWorkflowVersionMutations({
      partnerId,
      refetchWorkflowVersions,
    });

  const availability = useMemo(() => {
    const visibleVersions = workflowVersions.filter(
      (workflowVersion) => !workflowVersion.deletedAt,
    );
    const isOnlyVisibleVersion = visibleVersions.length <= 1;
    const isActive = !!selectedVersion.isActive;

    return {
      canActivate: !isActive,
      canDeactivate: isActive,
      canDelete: !isOnlyVisibleVersion,
      showActiveDeleteWarning: isActive,
    };
  }, [selectedVersion.isActive, workflowVersions]);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOpenMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleActivate = useCallback(async () => {
    closeMenu();

    if (!availability.canActivate || loading.isMutating) {
      return;
    }

    if (hasUnsavedChanges) {
      showErrorAlert('Save or discard your changes before activating this version.');
      return;
    }

    const result = await changeWorkflowVersionActiveState({
      workflowVersionId: selectedVersion.workflowVersionId,
      active: true,
      // Only one version may be active, so the others lose their dot the moment this one
      // gains it rather than when the refetch lands.
      siblingVersionIds: workflowVersions
        .map((workflowVersion) => workflowVersion.workflowVersionId)
        .filter((id) => id !== selectedVersion.workflowVersionId),
    });

    if (!result) {
      return;
    }

    showSuccessAlert(`Workflow version v${selectedVersion.version} activated`);
  }, [
    availability.canActivate,
    changeWorkflowVersionActiveState,
    closeMenu,
    loading.isMutating,
    selectedVersion.version,
    selectedVersion.workflowVersionId,
    showErrorAlert,
    showSuccessAlert,
    workflowVersions,
    hasUnsavedChanges,
  ]);

  const handleDeactivate = useCallback(async () => {
    closeMenu();

    if (!availability.canDeactivate || loading.isMutating) {
      return;
    }

    const result = await changeWorkflowVersionActiveState({
      workflowVersionId: selectedVersion.workflowVersionId,
      active: false,
    });

    if (!result) {
      return;
    }

    showSuccessAlert(`Workflow version v${selectedVersion.version} deactivated`);
  }, [
    availability.canDeactivate,
    changeWorkflowVersionActiveState,
    closeMenu,
    loading.isMutating,
    selectedVersion.version,
    selectedVersion.workflowVersionId,
    showSuccessAlert,
  ]);

  const handleDelete = useCallback(async () => {
    closeMenu();

    if (loading.isMutating) {
      return;
    }

    if (!availability.canDelete) {
      showErrorAlert('The last remaining workflow version cannot be deleted.');
      return;
    }

    const deleteBody = [
      `Version ${selectedVersion.version} of "${workflowName}" is hidden from the version selector and can't be used again.`,
      availability.showActiveDeleteWarning
        ? "If it's the active version, it's deactivated first."
        : null,
    ]
      .filter(Boolean)
      .join(' ');

    const shouldDelete = await confirm({
      title: 'Delete workflow version?',
      body: deleteBody,
      confirmLabel: 'Delete version',
      danger: true,
    });

    if (!shouldDelete) {
      return;
    }

    const didDelete = await softDeleteWorkflowVersion(selectedVersion.workflowVersionId);

    if (!didDelete) {
      return;
    }

    showSuccessAlert(`Workflow version v${selectedVersion.version} deleted`);
    onDeleteSuccess?.(selectedVersion.workflowVersionId);
  }, [
    availability.canDelete,
    availability.showActiveDeleteWarning,
    closeMenu,
    confirm,
    loading.isMutating,
    onDeleteSuccess,
    selectedVersion.version,
    selectedVersion.workflowVersionId,
    showErrorAlert,
    showSuccessAlert,
    softDeleteWorkflowVersion,
    workflowName,
  ]);

  return (
    <>
      <IconButton
        onClick={handleOpenMenu}
        disabled={viewOnly || loading.isMutating}
        aria-label="Version actions"
        size="small"
      >
        <DynamicIcon name="more-vertical" library="lucide" />
      </IconButton>

      <V2ThemeProvider>
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
          <MenuItem
            disabled={!availability.canActivate || loading.isMutating}
            onClick={handleActivate}
          >
            <ListItemIcon>
              <DynamicIcon name="circle-check" library="lucide" />
            </ListItemIcon>
            <ListItemText>Activate</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={!availability.canDeactivate || loading.isMutating}
            onClick={handleDeactivate}
          >
            <ListItemIcon>
              <DynamicIcon name="circle" library="lucide" />
            </ListItemIcon>
            <ListItemText>Deactivate</ListItemText>
          </MenuItem>
          <MenuItem
            className={v2MenuItemDangerClass}
            disabled={!availability.canDelete || loading.isMutating}
            onClick={handleDelete}
          >
            <ListItemIcon>
              <DynamicIcon name="trash-2" library="lucide" />
            </ListItemIcon>
            <ListItemText>Delete Version</ListItemText>
          </MenuItem>
        </Menu>
      </V2ThemeProvider>
    </>
  );
};
